import { EditorSelection } from "@codemirror/state";
import { EditorView, ViewPlugin } from "@codemirror/view";
import { syntaxTree } from "@codemirror/language";

import { collapsePreambleEffect } from "components/organisms/manuscript/editor/visual/rendering/widgets/preamble";

function skipAtomicRanges(state, rangeSets, pos) {
    let oldPos;

    do {
        oldPos = pos;

        for (const rangeSet of rangeSets) {
            let nextPos = pos;
            rangeSet.between(pos, pos, (_from, to) => {
                if (to > nextPos) {
                    nextPos = to;
                }
            });
            pos = nextPos;
        }

        if (pos !== oldPos && state.doc.lineAt(pos).to === pos) {
            pos += 1;
        }
    } while (pos !== oldPos);

    return Math.min(pos, state.doc.length);
}

export const skipPreambleWithCursor = (field) =>
    ViewPlugin.define((view) => {
        let checkedOnce = false;

        const escapeFromAtomicRanges = (selection, force = false) => {
            const originalSelection = selection;
            const atomicRangeSets = view.state
                .facet(EditorView.atomicRanges)
                .map((item) => (typeof item === "function" ? item(view) : item))
                .filter(Boolean);

            for (const [index, range] of selection.ranges.entries()) {
                const anchor = skipAtomicRanges(view.state, atomicRangeSets, range.anchor);
                const head = skipAtomicRanges(view.state, atomicRangeSets, range.head);

                if (anchor !== range.anchor || head !== range.head) {
                    selection = selection.replaceRange(EditorSelection.range(anchor, head), index);
                }
            }

            if (force || selection !== originalSelection) {
                window.setTimeout(() => {
                    view.dispatch({
                        selection,
                        scrollIntoView: true,
                    });
                });
            }
        };

        const escapeFromPreamble = () => {
            const preamble = view.state.field(field, false)?.preamble;
            if (preamble) {
                escapeFromAtomicRanges(EditorSelection.create([EditorSelection.cursor((preamble.to ?? 0) + 1)]), true);
            }
        };

        return {
            update(update) {
                if (checkedOnce) {
                    if (
                        update.transactions.some((transaction) =>
                            transaction.effects.some((effect) => effect.is(collapsePreambleEffect)),
                        )
                    ) {
                        escapeFromPreamble();
                    }
                } else if (syntaxTree(update.state).length === update.state.doc.length) {
                    checkedOnce = true;
                    if (update.state.selection.eq(EditorSelection.create([EditorSelection.cursor(0)]))) {
                        escapeFromPreamble();
                    } else {
                        escapeFromAtomicRanges(update.state.selection);
                    }
                }
            },
        };
    });
